/**
 * rescueOrphanSkinVertices — automatic skin-weight hygiene at the load door,
 * ported from v2's `rescueOrphanSkinVertices` (labs/retargeting.html, commit
 * 06b1d48) and widened to the defect that motivated the port (Allen's purple
 * furry monster, 2026-07-27: "one lone zero vector vert — it wasn't being
 * influenced").
 *
 * TWO defects, one symptom family:
 *   1. DEAD-BONE verts — Blender's glTF exporter binds auto-skinner-missed
 *      vertices to a synthetic `neutral_bone` parented to the Armature at
 *      origin, never animated: those verts freeze in world space while the
 *      body moves (v2's case — the red panda's floating glasses). A bone is
 *      dead when its parent is not a Bone AND it has no Bone children (the
 *      hierarchy root is excluded by its children).
 *   2. ZERO/NaN-SUM verts — no meaningful influence at all: the vert stays at
 *      bind pose (or collapses, engine-dependent). v2 only caught these by
 *      accident of slot encoding; here they are detected by the weight sum
 *      itself, NaN treated as no influence.
 *
 * The repair, per defective vert:
 *   - If its LIVE influences still carry ≥ 0.05 total weight, the dead/NaN
 *     slots are zeroed and the live remainder renormalized — the surviving
 *     legitimate skinning is kept (v2 hard-snapped these to one bone and lost
 *     it; the miner flagged that as its known flaw).
 *   - Otherwise, NEIGHBOR DONATION first (added 2026-07-27, Allen's furry
 *     monster): the vert inherits the averaged weights of its edge-connected
 *     non-orphan neighbors — the question being answered is "what does this
 *     vertex's FLESH do", and the neighbors ARE the flesh. Nearest-bone chose
 *     L_UpperarmTwist02 for two verts whose neighbors were 87% Head; donation
 *     cannot pick a wrong limb because it reads the surrounding skin directly.
 *   - Only when a vert has NO non-orphan neighbor (isolated debris) does it
 *     fall back to reseating wholly on the NEAREST live bone by bind-space
 *     distance (bone bind position = inverse of its boneInverse; the vertex
 *     carried through mesh.bindMatrix into the same space).
 *
 * Attributes are patched in place (`needsUpdate`), so everything downstream —
 * isolation, binding, EXPORT — inherits the repair. The caller receives a
 * per-mesh report and must surface it as evidence (LAWS: automatic is fine,
 * silent is not).
 *
 * Depends on: three. Depended on by: app session driver (run-document).
 */
import { Bone, Matrix4, Object3D, SkinnedMesh, Vector3 } from 'three';

export interface OrphanRescueReport {
  readonly mesh: string;
  /** Dead bones found in this mesh's skeleton (may be empty when only zero-sum verts occurred). */
  readonly deadBones: readonly string[];
  /** Verts that inherited their edge-neighbors' averaged weights (the preferred repair). */
  readonly donated: number;
  /** Donation dominant bones: bone name → vert count (each donated vert's top influence). */
  readonly donatedTo: Readonly<Record<string, number>>;
  /** Verts reseated wholly to the nearest live bone (no surviving live weight, no donor neighbor). */
  readonly reseated: number;
  /** Verts whose dead/NaN slots were dropped and surviving live weights renormalized. */
  readonly renormalized: number;
  /** Reseat destinations: bone name → vert count. */
  readonly reseatedTo: Readonly<Record<string, number>>;
}

/** Below this total live weight a vert counts as effectively uninfluenced. */
const MIN_LIVE_WEIGHT = 0.05;

const isBone = (o: Object3D | null): o is Bone => o !== null && (o as Bone).isBone === true;

export function rescueOrphanSkinVertices(root: Object3D): OrphanRescueReport[] {
  const meshes: SkinnedMesh[] = [];
  root.traverse((node) => {
    if ((node as SkinnedMesh).isSkinnedMesh === true) meshes.push(node as SkinnedMesh);
  });

  const reports: OrphanRescueReport[] = [];
  for (const mesh of meshes) {
    const position = mesh.geometry.getAttribute('position');
    const skinIndex = mesh.geometry.getAttribute('skinIndex');
    const skinWeight = mesh.geometry.getAttribute('skinWeight');
    if (!position || !skinIndex || !skinWeight) continue;
    const { bones, boneInverses } = mesh.skeleton;

    // Dead bones: structurally disconnected leaves (the neutral_bone shape).
    const dead = new Set<number>();
    if (bones.length > 1) {
      bones.forEach((bone, i) => {
        if (!isBone(bone.parent) && !bone.children.some((c) => isBone(c))) dead.add(i);
      });
    }

    // Live-bone bind positions, for nearest-bone reseating.
    const bindPos: (Vector3 | null)[] = bones.map((_, i) => {
      if (dead.has(i)) return null;
      const inv = boneInverses[i];
      if (inv === undefined) return null;
      return new Vector3().setFromMatrixPosition(new Matrix4().copy(inv).invert());
    });

    let donated = 0;
    let reseated = 0;
    let renormalized = 0;
    const donatedTo: Record<string, number> = {};
    const reseatedTo: Record<string, number> = {};
    const v = new Vector3();

    // --- pass 1: classify every vert (repairs must not shift later verdicts) ---
    const HEALTHY = 0;
    const RENORM = 1;
    const ORPHAN = 2;
    const cls = new Uint8Array(skinIndex.count);
    for (let vi = 0; vi < skinIndex.count; vi += 1) {
      let liveSum = 0;
      let defective = false;
      for (let k = 0; k < 4; k += 1) {
        const w = skinWeight.getComponent(vi, k);
        const bi = skinIndex.getComponent(vi, k);
        if (!Number.isFinite(w)) {
          defective = true;
        } else if (w > 0 && (dead.has(bi) || bi >= bones.length)) {
          defective = true;
        } else if (w > 0) {
          liveSum += w;
        }
      }
      cls[vi] = !defective && liveSum >= MIN_LIVE_WEIGHT ? HEALTHY : liveSum >= MIN_LIVE_WEIGHT ? RENORM : ORPHAN;
    }

    // --- pass 2: renormalize partials (surviving legitimate influences kept) ---
    for (let vi = 0; vi < skinIndex.count; vi += 1) {
      if (cls[vi] !== RENORM) continue;
      let liveSum = 0;
      for (let k = 0; k < 4; k += 1) {
        const w = skinWeight.getComponent(vi, k);
        const bi = skinIndex.getComponent(vi, k);
        if (Number.isFinite(w) && w > 0 && !dead.has(bi) && bi < bones.length) liveSum += w;
      }
      for (let k = 0; k < 4; k += 1) {
        const w = skinWeight.getComponent(vi, k);
        const bi = skinIndex.getComponent(vi, k);
        const liveOk = Number.isFinite(w) && w > 0 && !dead.has(bi) && bi < bones.length;
        skinWeight.setComponent(vi, k, liveOk ? w / liveSum : 0);
        if (!liveOk) skinIndex.setComponent(vi, k, 0);
      }
      renormalized += 1;
    }

    // --- pass 3: orphans — neighbor donation first, nearest-bone as last resort ---
    // Adjacency is built once, only when an orphan exists. Donors are the
    // orphan's edge-connected HEALTHY/RENORM verts, read AFTER pass 2 so a
    // donated row is always normalized legitimate skinning. Orphans never
    // donate to each other (deterministic — no traversal-order dependence).
    let adjacency: Map<number, Set<number>> | null = null;
    const buildAdjacency = (): Map<number, Set<number>> => {
      const adj = new Map<number, Set<number>>();
      const link = (a: number, b: number): void => {
        (adj.get(a) ?? adj.set(a, new Set()).get(a)!).add(b);
        (adj.get(b) ?? adj.set(b, new Set()).get(b)!).add(a);
      };
      const index = mesh.geometry.getIndex();
      const triCount = (index ? index.count : position.count) / 3;
      for (let t = 0; t < triCount; t += 1) {
        const a = index ? index.getX(t * 3) : t * 3;
        const b = index ? index.getX(t * 3 + 1) : t * 3 + 1;
        const c = index ? index.getX(t * 3 + 2) : t * 3 + 2;
        link(a, b);
        link(b, c);
        link(c, a);
      }
      return adj;
    };

    for (let vi = 0; vi < skinIndex.count; vi += 1) {
      if (cls[vi] !== ORPHAN) continue;
      adjacency ??= buildAdjacency();

      const pooled = new Map<number, number>();
      let donors = 0;
      for (const nb of adjacency.get(vi) ?? []) {
        if (cls[nb] === ORPHAN) continue;
        donors += 1;
        for (let k = 0; k < 4; k += 1) {
          const w = skinWeight.getComponent(nb, k);
          if (w <= 0) continue;
          const bi = skinIndex.getComponent(nb, k);
          pooled.set(bi, (pooled.get(bi) ?? 0) + w);
        }
      }
      if (donors > 0 && pooled.size > 0) {
        const top = [...pooled.entries()].sort((a, b) => b[1] - a[1]).slice(0, 4);
        const total = top.reduce((s, [, w]) => s + w, 0);
        for (let k = 0; k < 4; k += 1) {
          skinIndex.setComponent(vi, k, k < top.length ? top[k]![0] : 0);
          skinWeight.setComponent(vi, k, k < top.length ? top[k]![1] / total : 0);
        }
        donated += 1;
        const domName = bones[top[0]![0]]!.name;
        donatedTo[domName] = (donatedTo[domName] ?? 0) + 1;
        continue;
      }

      // No donor flesh around it — reseat to the nearest live bone in bind space.
      v.fromBufferAttribute(position, vi).applyMatrix4(mesh.bindMatrix);
      let best = -1;
      let bestD = Infinity;
      for (let bi = 0; bi < bones.length; bi += 1) {
        const p = bindPos[bi];
        if (p === null || p === undefined) continue;
        const d = v.distanceToSquared(p);
        if (d < bestD) {
          bestD = d;
          best = bi;
        }
      }
      if (best < 0) continue; // no viable live bone — leave untouched (reported counts stay honest)
      skinIndex.setComponent(vi, 0, best);
      skinWeight.setComponent(vi, 0, 1);
      for (let k = 1; k < 4; k += 1) {
        skinIndex.setComponent(vi, k, 0);
        skinWeight.setComponent(vi, k, 0);
      }
      reseated += 1;
      const name = bones[best]!.name;
      reseatedTo[name] = (reseatedTo[name] ?? 0) + 1;
    }

    if (donated > 0 || reseated > 0 || renormalized > 0) {
      skinIndex.needsUpdate = true;
      skinWeight.needsUpdate = true;
      reports.push({
        mesh: mesh.name || '(unnamed mesh)',
        deadBones: [...dead].map((i) => bones[i]!.name),
        donated,
        donatedTo,
        reseated,
        renormalized,
        reseatedTo,
      });
    }
  }
  return reports;
}
